import { FileIcon, Upload, X } from "lucide-react";
import React, { useState } from "react";
import { Accept, useDropzone } from "react-dropzone";
import {
  FieldValues,
  Path,
  useController,
  UseControllerProps,
} from "react-hook-form";
import { toast } from "sonner";
import { useFileUpload } from "../../packages/hooks/use-file-upload";
import "./styles/controlled-dropzone.css";

/**
 * ControlledDropzone - A reusable file dropzone component that integrates with react-hook-form
 *
 * Features:
 * - Drag and drop file upload
 * - File validation (type, size, count)
 * - Image previews for image files
 * - Support for existing files
 * - Automatic file upload to server
 * - Upload progress tracking
 * - Customizable styling
 * - Error handling with toast notifications
 * - Support for both single and multiple files
 *
 * @example
 * ```tsx
 * <ControlledDropzone
 *   control={control}
 *   name="image"
 *   label="Upload Image"
 *   maxFiles={1}
 *   maxSizePerFile={10}
 *   accept={{ "image/*": [".jpeg", ".jpg", ".png", ".webp", ".gif"] }}
 *   autoUpload={true}
 *   uploadFolder="categories"
 *   existingFiles={existingImages}
 *   onRemoveExisting={handleRemoveExisting}
 *   onUploadComplete={(results) => handleUploadComplete(results)}
 * />
 * ```
 */

export type ControlledDropzoneProps<T extends FieldValues> = Omit<
  UseControllerProps<T>,
  "name" | "control"
> & {
  control: UseControllerProps<T>["control"];
  name: Path<T>;
  label?: string;
  helperText?: string;
  maxFiles?: number;
  maxSizePerFile?: number; // in MB
  accept?: Accept;
  multiple?: boolean;
  onFilesAccepted?: (files: File[]) => void;
  onFilesRejected?: (rejections: any[]) => void;
  className?: string;
  dropzoneClassName?: string;
  previewClassName?: string;
  showPreviews?: boolean;
  existingFiles?: string[];
  onRemoveExisting?: (url: string) => void;
  disabled?: boolean;
  required?: boolean;
  // Upload options
  autoUpload?: boolean;
  uploadFolder?: string;
  onUploadComplete?: (results: any[]) => void;
  onUploadError?: (errors: string[]) => void;
  showUploadProgress?: boolean;
};

export function ControlledDropzone<T extends FieldValues>({
  control,
  name,
  defaultValue,
  rules,
  shouldUnregister,
  label,
  helperText,
  maxFiles = 1,
  maxSizePerFile = 5,
  accept = {
    "image/*": [".jpeg", ".jpg", ".png", ".webp"],
  },
  multiple = false,
  onFilesAccepted,
  onFilesRejected,
  className = "",
  dropzoneClassName = "",
  previewClassName = "",
  showPreviews = true,
  existingFiles = [],
  onRemoveExisting,
  autoUpload = true,
  uploadFolder = "uploads",
  onUploadComplete,
  onUploadError,
  showUploadProgress = true,
  ...inputProps
}: ControlledDropzoneProps<T>) {
  const {
    field: { value, onChange },
    fieldState: { error },
  } = useController<T>({
    control: control!,
    name,
    ...(defaultValue !== undefined && { defaultValue }),
    ...(rules !== undefined && { rules }),
    ...(shouldUnregister !== undefined && { shouldUnregister }),
  });

  // Track image load errors
  const [imageErrors, setImageErrors] = useState<{ [key: string]: boolean }>(
    {}
  );

  // Upload functionality
  const { uploadFiles, uploadProgress } = useFileUpload({
    folder: uploadFolder,
    onUploadComplete: (results) => {
      if (results.length > 0 && results[0]) {
        if (multiple) {
          // For multiple files, store URLs in array
          const currentFiles = value
            ? Array.isArray(value)
              ? value
              : [value]
            : [];
          const newFiles = results.map((r) => r.url);
          onChange([...currentFiles, ...newFiles] as T[typeof name]);
        } else {
          // For single file, store only the URL string
          const firstResult = results[0];
          onChange(firstResult.url as T[typeof name]);
        }
      }
      onUploadComplete?.(results);
    },
    onUploadError: (errors) => {
      onUploadError?.(errors);
    },
  });

  const onDrop = React.useCallback(
    (acceptedFiles: File[], fileRejections: any[]) => {
      if (fileRejections.length > 0) {
        const errorMessages = fileRejections.map((rejection) => {
          const rejectionErrors = rejection.errors.map(
            (err: any) => err.message
          );
          return `${rejection.file.name}: ${rejectionErrors.join(", ")}`;
        });
        toast.error(`Ошибки загрузки:\n${errorMessages.join("\n")}`);
        onFilesRejected?.(fileRejections);
        return;
      }

      if (acceptedFiles.length > 0) {
        // Check total file count
        const currentFileCount = value
          ? Array.isArray(value)
            ? value.length
            : 1
          : 0;
        const totalFiles =
          currentFileCount + existingFiles.length + acceptedFiles.length;
        if (totalFiles > maxFiles) {
          toast.error(`Разрешено максимум ${maxFiles} файлов`);
          return;
        }

        onFilesAccepted?.(acceptedFiles);

        if (autoUpload) {
          // Auto-upload files
          uploadFiles(acceptedFiles);
        } else {
          // Store files without uploading
          if (!multiple && acceptedFiles.length > 0) {
            onChange(acceptedFiles[0] as T[typeof name]);
          } else {
            const currentFiles = value
              ? Array.isArray(value)
                ? value
                : [value]
              : [];
            onChange([...currentFiles, ...acceptedFiles] as T[typeof name]);
          }
          toast.success(`Добавлено ${acceptedFiles.length} файл(ов)`);
        }
      }
    },
    [
      value,
      onChange,
      maxFiles,
      multiple,
      existingFiles.length,
      onFilesAccepted,
      onFilesRejected,
      autoUpload,
      uploadFiles,
    ]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept,
    multiple,
    maxFiles: multiple ? maxFiles : 1,
    maxSize: maxSizePerFile * 1024 * 1024, // Convert MB to bytes
    ...(inputProps.disabled !== undefined && { disabled: inputProps.disabled }),
  });

  const removeFile = (index: number) => {
    if (Array.isArray(value)) {
      const newFiles = value.filter((_: any, i: number) => i !== index);
      onChange(newFiles);
    } else {
      onChange(null);
    }
  };

  const removeExistingFile = (url: string) => {
    onRemoveExisting?.(url);
  };

  const currentFiles = value ? (Array.isArray(value) ? value : [value]) : [];

  // Helper function to get file URL from either string or object
  const getFileUrl = (file: any): string => {
    return typeof file === "string" ? file : file?.url || "";
  };

  // Helper function to get file size from file object
  const getFileSize = (file: any): number | null => {
    return typeof file === "object" && file?.size ? file.size : null;
  };

  // Helper function to check if URL is an image (supports Firebase, Yandex, and other cloud storage URLs)
  const isImageUrl = (url: string): boolean => {
    if (!url) return false;

    // Check for image file extensions (with or without query parameters)
    const imageExtensions =
      /\.(jpg|jpeg|png|gif|webp|bmp|svg|avif|tiff?)(\?.*)?$/i;
    if (imageExtensions.test(url)) return true;

    // Special handling for Firebase Storage URLs
    if (url.includes("firebasestorage.googleapis.com")) {
      // Firebase URLs contain the filename in the path before query parameters
      const pathMatch = url.match(/\/o\/[^?]*%2F([^?%]+)/);
      if (pathMatch && pathMatch[1]) {
        const filename = decodeURIComponent(pathMatch[1]);
        return /\.(jpg|jpeg|png|gif|webp|bmp|svg|avif|tiff?)$/i.test(filename);
      }
    }

    // Special handling for other cloud storage services
    if (
      url.includes("storage.yandexcloud.net") ||
      url.includes("s3.amazonaws.com") ||
      url.includes("storage.googleapis.com") ||
      url.includes("blob.core.windows.net")
    ) {
      return imageExtensions.test(url);
    }

    return false;
  };
  const totalFiles = currentFiles.length + existingFiles.length;
  const canAddMore = totalFiles < maxFiles && !inputProps.disabled;

  return (
    <div
      className={`form-field controlled-dropzone ${
        error ? "form-field--error" : ""
      } ${className}`}
    >
      {label && (
        <span
          className={`form-field__label ${
            inputProps.required ? "form-field__label--required" : ""
          }`}
        >
          {label}
        </span>
      )}

      {/* Dropzone Area */}
      <div
        {...getRootProps()}
        className={`
          controlled-dropzone__dropzone
          ${isDragActive ? "controlled-dropzone__dropzone--active" : ""}
          ${!canAddMore ? "controlled-dropzone__dropzone--disabled" : ""}
          ${
            inputProps.disabled ? "controlled-dropzone__dropzone--disabled" : ""
          }
          ${dropzoneClassName}
        `}
      >
        <input {...getInputProps()} className="controlled-dropzone__input" />

        <div className="controlled-dropzone__content">
          <Upload
            className={`controlled-dropzone__icon ${
              canAddMore ? "" : "controlled-dropzone__icon--disabled"
            }`}
          />
          <div className="controlled-dropzone__text">
            {canAddMore ? (
              <>
                <span className="controlled-dropzone__text--primary">
                  Нажмите для загрузки
                </span>{" "}
                или перетащите файлы
                <br />
                <span className="controlled-dropzone__text--secondary">
                  {Object.keys(accept).join(", ")} до {maxSizePerFile}МБ каждый
                  {multiple ? ` (макс. ${maxFiles} файлов)` : ""}
                </span>
              </>
            ) : (
              <span className="controlled-dropzone__text--disabled">
                Достигнуто максимум {maxFiles} файлов
              </span>
            )}
          </div>
        </div>
      </div>

      {/* File Previews */}
      {showPreviews &&
        (existingFiles.length > 0 || currentFiles.length > 0) && (
          <div className={`controlled-dropzone__previews ${previewClassName}`}>
            <h4 className="controlled-dropzone__previews-title">
              Файлы ({totalFiles}/{maxFiles})
            </h4>

            <div className="controlled-dropzone__previews-grid">
              {/* Existing Files */}
              {existingFiles.map((url, index) => {
                const errorKey = `existing-${index}`;
                return (
                  <div
                    key={errorKey}
                    className="controlled-dropzone__preview-item"
                  >
                    {!imageErrors[errorKey] && isImageUrl(url) ? (
                      <img
                        src={url}
                        alt={`Существующий файл ${index + 1}`}
                        className="controlled-dropzone__preview-image"
                        onError={() => {
                          setImageErrors((prev) => ({
                            ...prev,
                            [errorKey]: true,
                          }));
                        }}
                      />
                    ) : imageErrors[errorKey] ? (
                      <div className="flex flex-col items-center justify-center text-center p-2">
                        <FileIcon className="h-8 w-8 text-red-400 mb-1" />
                        <span className="text-xs text-red-500">
                          Не удалось загрузить
                        </span>
                        <span className="text-xs text-gray-400 break-all">
                          {url}
                        </span>
                      </div>
                    ) : (
                      <FileIcon className="controlled-dropzone__preview-icon" />
                    )}
                    {onRemoveExisting && (
                      <button
                        type="button"
                        onClick={() => removeExistingFile(url)}
                        className="controlled-dropzone__preview-remove"
                        disabled={inputProps.disabled}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    )}
                    <div className="controlled-dropzone__preview-badge controlled-dropzone__preview-badge--saved">
                      Сохранено
                    </div>
                  </div>
                );
              })}

              {/* New Files */}
              {currentFiles.map((file: any, index: number) => {
                const fileUrl = getFileUrl(file);
                const fileSize = getFileSize(file);
                const errorKey = `new-${index}`;

                return (
                  <div
                    key={errorKey}
                    className="controlled-dropzone__preview-item"
                  >
                    {!imageErrors[errorKey] && isImageUrl(fileUrl) ? (
                      <img
                        src={fileUrl}
                        alt={`Новый файл ${index + 1}`}
                        className="controlled-dropzone__preview-image"
                        onError={() => {
                          setImageErrors((prev) => ({
                            ...prev,
                            [errorKey]: true,
                          }));
                        }}
                      />
                    ) : imageErrors[errorKey] ? (
                      <div className="flex flex-col items-center justify-center text-center p-2">
                        <FileIcon className="h-8 w-8 text-red-400 mb-1" />
                        <span className="text-xs text-red-500">
                          Не удалось загрузить
                        </span>
                        <span className="text-xs text-gray-400 break-all">
                          {fileUrl}
                        </span>
                      </div>
                    ) : (
                      <FileIcon className="controlled-dropzone__preview-icon" />
                    )}
                    <button
                      type="button"
                      onClick={() => removeFile(index)}
                      className="controlled-dropzone__preview-remove"
                      disabled={inputProps.disabled}
                    >
                      <X className="h-3 w-3" />
                    </button>
                    <div className="controlled-dropzone__preview-badge controlled-dropzone__preview-badge--new">
                      Новый
                    </div>
                    {fileSize && (
                      <div className="controlled-dropzone__preview-size">
                        {(fileSize / 1024 / 1024).toFixed(1)}MB
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

      {/* Upload Progress */}
      {showUploadProgress && uploadProgress.length > 0 && (
        <div className="controlled-dropzone__progress">
          <h4 className="controlled-dropzone__progress-title">
            Прогресс загрузки
          </h4>
          {uploadProgress.map((progress, index) => (
            <div key={index} className="controlled-dropzone__progress-item">
              <div className="controlled-dropzone__progress-header">
                <span>{progress.file.name}</span>
                <span className="capitalize">{progress.status}</span>
              </div>
              <div className="controlled-dropzone__progress-bar">
                <div
                  className={`controlled-dropzone__progress-fill ${
                    progress.status === "success"
                      ? "controlled-dropzone__progress-fill--success"
                      : progress.status === "error"
                      ? "controlled-dropzone__progress-fill--error"
                      : "controlled-dropzone__progress-fill--uploading"
                  }`}
                  style={{ width: `${progress.progress}%` }}
                />
              </div>
              {progress.error && (
                <span className="controlled-dropzone__progress-error">
                  {progress.error}
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      {helperText && <span className="form-field__helper">{helperText}</span>}
      {error && <span className="form-field__error">{error.message}</span>}
    </div>
  );
}
